import { FC, useEffect } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import triggerTracking from '@blueprint/tracking';
import { GoogleMedium, FacebookMedium } from '@zoopla-eng/zds-components/icons';
import {
    SocialSignInText,
    SignInErrorMessage,
    SocialSignInTextWrapper
} from '../../AuthModal.styles';
import { ActionAfterAuth } from '../../AuthModal.types';
import { useSocialSignIn } from './hooks/useSocialSignIn/useSocialSignIn';
import ErrorAlert from '../../ErrorAlert/ErrorAlert';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { Grid } from '@zoopla-eng/zds-components/grid';

type SocialCopy = {
    cta?: string;
    facebook: string;
    google: string;
};

export type LoadingType = Partial<Record<'facebook' | 'google', boolean>>;

export type SocialAuthComponentProps = {
    actionAfterAuth: ActionAfterAuth;
    copy: SocialCopy;
    type: 'signin' | 'register';
    shouldReset?: boolean;
    resetErrors?: () => void;
    variant?: 'horizontal' | 'vertical';
    onClickTracking?: (clickType: 'facebook' | 'google') => void;
    isRememberMeChecked?: boolean;
};

const SocialAuth: FC<React.PropsWithChildren<SocialAuthComponentProps>> = ({
    actionAfterAuth,
    copy,
    type,
    shouldReset,
    resetErrors,
    onClickTracking,
    isRememberMeChecked,
    variant = 'horizontal'
}) => {
    const [
        facebookError,
        onFacebookRegister,
        isFacebookAuthenticating,
        resetFacebookError
    ] = useSocialSignIn({
        type: 'facebook',
        actionAfterAuth
    });

    const [
        googleError,
        onGoogleRegister,
        isGoogleAuthenticating,
        resetGoogleError
    ] = useSocialSignIn({
        type: 'google',
        actionAfterAuth
    });

    useEffect(() => {
        if (isFacebookAuthenticating || isGoogleAuthenticating) {
            resetErrors?.();
            return;
        }

        if (shouldReset) {
            resetGoogleError();
            resetFacebookError();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFacebookAuthenticating, isGoogleAuthenticating, shouldReset]);

    const socialError = googleError || facebookError;

    return (
        <>
            <ErrorAlert hasError={!!socialError}>
                {socialError && (
                    <Box pb="$S400">
                        <SignInErrorMessage>{socialError}</SignInErrorMessage>
                    </Box>
                )}
            </ErrorAlert>
            <Grid
                gridTemplateColumns={
                    variant === 'horizontal' ? 'auto auto' : 'auto'
                }
                gridColumnGap="$S400"
                gridRowGap="$S400"
            >
                <ButtonLeft
                    icon={<GoogleMedium />}
                    category="secondary"
                    data-testid={`${type}-google`}
                    onClick={() => {
                        onGoogleRegister(isRememberMeChecked);

                        if (onClickTracking) {
                            onClickTracking('google');
                            return;
                        }

                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'google'
                        });
                    }}
                    loading={isGoogleAuthenticating}
                    loadingText="Loading"
                >
                    {copy.google}
                </ButtonLeft>
                <ButtonLeft
                    icon={<FacebookMedium />}
                    category="secondary"
                    data-testid={`${type}-facebook`}
                    onClick={() => {
                        onFacebookRegister(isRememberMeChecked);

                        if (onClickTracking) {
                            onClickTracking('facebook');
                            return;
                        }

                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: type,
                            eventAction: 'click',
                            eventLabel: 'facebook'
                        });
                    }}
                    loading={isFacebookAuthenticating}
                    loadingText="Loading"
                >
                    {copy.facebook}
                </ButtonLeft>
            </Grid>
            {copy.cta && (
                <Box pb="$M300" pt="$M200">
                    <SocialSignInTextWrapper>
                        <Box px="$S200">
                            <SocialSignInText>{copy.cta}</SocialSignInText>
                        </Box>
                    </SocialSignInTextWrapper>
                </Box>
            )}
        </>
    );
};

export default SocialAuth;
